/**
 * BundleDrafts — the "My drafts" companion panel under BundleBasket (BM-H254).
 *
 * Presentational: parent (FindDocument) owns the drafts list and passes it in,
 * plus onOpen and onDelete callbacks that call the bundle API and refresh state.
 * Each row shows name, optional matter ref, item count, and updated_at.
 * Delete is a small X on hover, confirmed via window.confirm before firing.
 */
import { FolderOpen, X, FileStack } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function BundleDrafts({
  drafts = [],
  busy = false,
  onOpen,
  onDelete,
}) {
  return (
    <aside
      data-testid="bundle-drafts"
      className="w-full rounded-lg border border-navy-border bg-navy-card p-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <FileStack className="text-teal" size={16} />
        <h2 className="text-sm font-semibold text-white">My drafts</h2>
        <span
          data-testid="bundle-drafts-count"
          className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-teal/20 text-teal border border-teal"
        >
          {drafts.length}
        </span>
      </div>

      {drafts.length === 0 ? (
        <p
          data-testid="bundle-drafts-empty"
          className="text-xs text-slate-500 py-4 text-center"
        >
          Saved drafts will appear here.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {drafts.map((d) => (
            <li
              key={d.draft_id}
              data-testid={`bundle-draft-${d.draft_id}`}
              className="group flex items-center gap-1.5 rounded-md border border-navy-border bg-navy-hover px-2 py-1.5"
            >
              <button
                type="button"
                data-testid={`bundle-draft-open-${d.draft_id}`}
                onClick={() => onOpen(d.draft_id)}
                disabled={busy}
                title="Open this draft into the basket"
                className="flex-1 min-w-0 text-left disabled:opacity-50"
              >
                <div className="flex items-center gap-1.5">
                  <FolderOpen size={12} className="text-slate-400 shrink-0" />
                  <span className="truncate text-xs text-white">{d.name || "Untitled draft"}</span>
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-[10px] text-slate-400">
                  {d.matter_ref && (
                    <span className="font-mono truncate">{d.matter_ref}</span>
                  )}
                  <span>{d.count} item{d.count === 1 ? "" : "s"}</span>
                  {d.updated_at && <span>· {formatDate(d.updated_at)}</span>}
                </div>
              </button>
              <button
                type="button"
                data-testid={`bundle-draft-delete-${d.draft_id}`}
                onClick={() => onDelete(d)}
                disabled={busy}
                title="Delete this draft"
                className="text-slate-400 hover:text-red-300 shrink-0 opacity-40 group-hover:opacity-100 transition-opacity"
                aria-label={`Delete draft ${d.name || d.draft_id}`}
              >
                <X size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}
